import { isEmpty } from 'ramda';
import types from '../constants/parameterTypes';
import {
    mappingSource,
    validateMappingName,
} from '../helpers/sharePointMappingSource';
import submitter from './SubmitToSharePointList.submitter';

const optionalParameters = [
    {
        name: 'mapping',
        description: 'Mapping Data Connection Name - default is mapping',
        type: types.typeDataSource,
    },
    {
        name: 'mappingName',
        description:
            'Name of mapping to submit if not submitting all mappings in a mapping file',
    },
    {
        name: 'id',
        description:
            'Path to Id field for SharePoint List Id (required if adding attachments, pre-4.2 mapping - can be indicated in mapping file for 4.2 and later.)',
    },
    {
        name: 'dsname',
        description:
            'Data Source For AddAttachment Method (SharePoint Lists Web Service)',
        type: types.typeDataSource,
    },
    {
        name: 'xpath',
        description:
            'XPath to attachment field  (pre-4.2 mapping - can be indicated in mapping file for v4.2 and later)',
        type: types.typeXPath,
    },
    {
        name: 'name',
        description: 'Name for file (extension will be added by qRules)',
    },
    {
        name: 'namexpath',
        description: 'Relative XPath for name field',
        type: types.typeXPath,
        relativeTo: 'xpath',
    },
    {
        name: 'uniqueName',
        description:
            'Prepend date and time information to the file name to provide a unique name (yes | no)',
        type: types.typeBoolean,
    },
    {
        name: 'clear',
        description:
            '(yes | no)- indicates whether to clear the attachment field on successful upload. Default is yes.',
        type: types.typeBoolean,
    },
    {
        name: 'ssappid',
        description:
            'Secure store application id to use if user is logged in anonymously',
    },
    {
        name: 'submit',
        description: 'Sharepoint List Service Submit Connection Name',
        type: types.typeDataConnection,
    },
];
const requiredParameters = [];
const description =
    'Submits data to the SharePoint list it has been mapped to. If provided with the path to a field for the list item ID, populates that, allowing for updating of items in the list from the form. Requires use of InfoPath to SharePoint List Tool for mapping. This command, like RefreshSharePointListItems, underwent major revisions with qRules v4.2. The command syntax as documented below works with qRules v4.2 and later, and requires the use of the updated mapping form that installs with qRules v4.2. Previous versions of the command will continue to work without any changes. More information can be found on http://www.infopathdev.com/blogs/hilary/archive/2012/05/30/qrules-submittosharepointlist-command-changes-for-v4-2.aspx. Additional information on the structure of the mapping file can be found on http://www.infopathdev.com/blogs/hilary/archive/2012/05/30/qrules-submittosharepointlist-command-general-overview.aspx. A walkthrough blog post http://www.infopathdev.com/blogs/hilary/archive/2012/05/30/qrules-submittosharepointlist-walkthrough-for-v4-2.aspx is also available.';

function SubmitToSharePointList(params) {
    async function performSubmitToList(settings) {
        const ds = params.dataSources.getDom();
        let errors = [];

        for (const mapping of settings.mappings.topLevelMappings) {
            const submitterObj = submitter(params.shpAccess);
            const baseNodes = [
                {
                    node: ds,
                    id: '',
                },
            ];
            const resultErrors = await submitterObj.performSubmitAsync(
                mapping,
                settings,
                baseNodes,
            );

            //return if no data i.e. no mapping found.
            if (resultErrors && resultErrors.length) {
                errors = errors.concat(resultErrors);
            }
        }

        return errors;
    }

    async function executeAsync() {
        const commonFunction = params.commonFunction;
        const { getDataSource, getBoolParamValue, getParamValue } =
            commonFunction;
        const mappingDsName = getParamValue('mapping', 'mapping');
        const mappingName = getParamValue('mappingName', '');
        // 2021-08-31 Removed unused ssappid
        let settings = {
            submitDsName: getParamValue('submit'),
            idxPath: getParamValue('id', ''),
            attachSettings: {
                xPath: getParamValue('xpath', ''),
                name: getParamValue('name', ''),
                nameXPath: getParamValue('namexpath'),
                clear: getBoolParamValue('clear', true),
                uniqueName: getBoolParamValue('uniqueName', false),
                dsName: getParamValue('dsname', ''),
            },
        };
        const mappingDs = getDataSource(mappingDsName);

        //just verify the submit at this point - we will verify the mapping when the mapping nodes are returned
        commonFunction.verifyDataConnectionExists(settings.submitDsName);

        settings.mappings = mappingSource(mappingDs, mappingName);

        validateMappingName(mappingName, settings.mappings);

        const data = await performSubmitToList(settings);

        if (data && !isEmpty(data)) {
            return { error: `Errors encountered: ${data.join('; ')}` };
        }

        return { success: true };
    }

    return {
        description: description,
        executeAsync: executeAsync,
        optionalParameters: optionalParameters,
        requiredParameters: requiredParameters,
    };
}

export default SubmitToSharePointList;
